export default function SarasotaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Sarasota Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Sarasota travelers actually move helps us anticipate the passport urgencies that arise here. From performing arts touring schedules to Ringling College study abroad cycles, the Cultural Coast operates on a different rhythm than most Florida markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* SRQ Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">SRQ — Sarasota-Bradenton International</h3>
                <p className="text-gray-400 text-xs">North Sarasota, primary airport for the region</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              SRQ has expanded international service significantly, with seasonal flights to Toronto and connections to European hubs through major carriers. Arts travelers, Ringling College students, and cultural tourists increasingly use SRQ for international departures. The airport's growth mirrors Sarasota's rise as a cultural destination — but many travelers still do not realize their passport needs until they are booking a flight to Florence for a semester abroad or London for a theatre festival.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking SRQ international departures. If it expires within six months of your return, renew it first — or call us for expedited processing before your arts tour or study abroad program.
              </p>
            </div>
          </div>

          {/* Tampa International */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">TPA — Tampa International Airport</h3>
                <p className="text-gray-400 text-xs">About 45 minutes north of Sarasota via I-75</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Many Sarasota arts travelers and Ringling College students use Tampa International as their primary gateway to Europe and Asia. TPA offers robust international connections through major hub carriers, making it the preferred departure point for gallery tours, performing arts festivals, and extended cultural travel. The drive up I-75 is routine for seasoned Sarasota travelers — but discovering an expired passport the night before a departure is not.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Tampa International is a major hub — but that also means higher passenger volume and stricter document checks. Ensure your passport has at least two blank pages and six months validity before departing through TPA.
              </p>
            </div>
          </div>

          {/* Arts Season */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-calendar-event-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Arts Season &amp; Winter Resident Calendar</h3>
                <p className="text-gray-400 text-xs">October–April peak cultural activity</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Sarasota's performing arts season runs October through April, with the Sarasota Opera, Sarasota Ballet, Asolo Repertory Theatre, and Sarasota Orchestra all on active international schedules. Winter residents arrive precisely for this cultural calendar — and often discover passport issues while preparing for holiday trips to European Christmas markets or spring gallery tours. The overlap of arts season and snowbird migration creates a predictable passport demand spike that government offices are not staffed to handle.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Renew before October if your passport expires during arts season. Government wait times stretch when snowbirds and arts travelers flood the system simultaneously. Our courier processing stays predictable year-round.
              </p>
            </div>
          </div>

          {/* Ringling College & Study Abroad */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Ringling College &amp; Study Abroad Cycles</h3>
                <p className="text-gray-400 text-xs">Semester abroad programs in Florence, Paris, Tokyo</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Ringling College of Art and Design sends students abroad every semester — primarily to Florence, Paris, and Tokyo for art history, design research, and animation studies. Parents often discover passport problems during the pre-departure scramble: expired passports, name mismatches after recent marriages, or first-time applications for students who have never traveled internationally. Program deadlines are non-negotiable, and standard post office processing rarely aligns with academic calendars.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your student's passport six months before any study abroad program. Many Ringling programs require passports valid for six months beyond the program end date. We process student expedited applications with the same priority as any other case.
              </p>
            </div>
          </div>
        </div>

        {/* Cruise callout */}
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-ship-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Caribbean Cruises from Tampa &amp; Port Canaveral</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Sarasota residents regularly drive to Tampa or Port Canaveral for Caribbean and repositioning cruises. Many book during flash sales 30–60 days out — then discover passport validity issues. Closed-loop cruises allow birth certificates, but any international itinerary or flight connection requires a valid passport. We accommodate tight cruise departure timelines with processing as fast as 24 hours when needed.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}